import React, {useMemo, useState} from 'react';
import {Box, Container, Paper, Stack, Tab, Tabs, Typography} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BrochurePdf from './BrochurePdf';
import {DASH_BROCHURE_MARKDOWN} from './dashBrochureContent';
import {buildHelpMarkdownComponents} from './helpMarkdownComponents';
import {HELP_CONTAINER_SX, HELP_PAGE_SX, HELP_PANEL_SX} from './helpPanelStyles';

export const HELP_TABS = [
    {
        id: 0,
        label: 'Product brochure',
        caption: 'Visual overview of DASH',
        icon: PictureAsPdfOutlinedIcon,
    },
    {
        id: 1,
        label: 'Platform manual',
        caption: 'Features, roles & workflows',
        icon: MenuBookOutlinedIcon,
    },
];

function HelpTabLabel({label, caption, Icon, selected}) {
    return (
        <Stack alignItems="center" spacing={0.25} sx={{py: 0.5}}>
            <Icon
                sx={{
                    fontSize: 22,
                    color: selected ? 'primary.main' : 'text.secondary',
                }}
            />
            <Typography component="span" variant="body2" fontWeight={700} lineHeight={1.3}>
                {label}
            </Typography>
            <Typography
                component="span"
                variant="caption"
                sx={{
                    color: selected ? 'primary.main' : 'text.secondary',
                    lineHeight: 1.2,
                    opacity: selected ? 0.9 : 0.85,
                }}
            >
                {caption}
            </Typography>
        </Stack>
    );
}

const HelpPage = () => {
    const [tab, setTab] = useState(0);
    const markdownComponents = useMemo(() => buildHelpMarkdownComponents(), []);

    return (
        <Box sx={HELP_PAGE_SX}>
            <Container maxWidth="lg" disableGutters sx={HELP_CONTAINER_SX}>
                <Paper
                    elevation={0}
                    sx={{
                        border: HELP_PANEL_SX.border,
                        borderColor: HELP_PANEL_SX.borderColor,
                        borderRadius: HELP_PANEL_SX.borderRadius,
                        overflow: HELP_PANEL_SX.overflow,
                    }}
                >
                    <Tabs
                        value={tab}
                        onChange={(_, value) => setTab(value)}
                        variant="fullWidth"
                        aria-label="Help content"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            minHeight: 88,
                            '& .MuiTabs-flexContainer': {width: '100%'},
                            '& .MuiTabs-indicator': {height: 3, borderRadius: '3px 3px 0 0'},
                            '& .MuiTab-root': {
                                minHeight: 88,
                                minWidth: 0,
                                flex: '1 1 0',
                                textTransform: 'none',
                                opacity: 1,
                                color: 'text.secondary',
                                transition: 'background-color 150ms ease, color 150ms ease',
                                '&.Mui-selected': {
                                    color: 'text.primary',
                                    bgcolor: 'primary.50',
                                },
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                                '&.Mui-selected:hover': {
                                    bgcolor: 'primary.100',
                                },
                            },
                        }}
                    >
                        {HELP_TABS.map(({id, label, caption, icon: Icon}) => (
                            <Tab
                                key={id}
                                value={id}
                                disableRipple
                                label={
                                    <HelpTabLabel
                                        label={label}
                                        caption={caption}
                                        Icon={Icon}
                                        selected={tab === id}
                                    />
                                }
                            />
                        ))}
                    </Tabs>

                    <Box
                        role="tabpanel"
                        aria-labelledby={`help-tab-${tab}`}
                        sx={{p: HELP_PANEL_SX.p}}
                    >
                        {tab === 0 ? (
                            <BrochurePdf/>
                        ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {DASH_BROCHURE_MARKDOWN}
                            </ReactMarkdown>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default HelpPage;
